/* Bamgum – karuseller, galleri, före/efter och köpruta */

if (!customElements.get('bg-slider')) {
  customElements.define(
    'bg-slider',
    class BgSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-bg-track]');
        if (!this.track) return;
        this.prev = this.querySelector('[data-bg-prev]');
        this.next = this.querySelector('[data-bg-next]');
        this.dotsWrap = this.querySelector('[data-bg-dots]');
        this.prev?.addEventListener('click', () => this.go(-1));
        this.next?.addEventListener('click', () => this.go(1));
        this.track.addEventListener('scroll', () => this.update(), { passive: true });
        window.addEventListener('resize', () => this.buildDots());
        this.buildDots();
        this.update();
      }

      get slides() {
        return Array.from(this.track.children);
      }

      get step() {
        const first = this.slides[0];
        if (!first) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      }

      get pages() {
        const perView = Math.max(1, Math.round(this.track.clientWidth / this.step));
        return Math.max(1, this.slides.length - perView + 1);
      }

      go(dir) {
        this.track.scrollBy({ left: dir * this.step, behavior: 'smooth' });
      }

      buildDots() {
        if (!this.dotsWrap) return;
        this.dotsWrap.innerHTML = '';
        const count = this.pages;
        if (count < 2) return;
        for (let i = 0; i < count; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'bg-slider__dot';
          dot.setAttribute('aria-label', `${i + 1} / ${count}`);
          dot.addEventListener('click', () => this.track.scrollTo({ left: i * this.step, behavior: 'smooth' }));
          this.dotsWrap.appendChild(dot);
        }
        this.update();
      }

      update() {
        const index = Math.round(this.track.scrollLeft / this.step);
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max;
        this.dotsWrap?.querySelectorAll('.bg-slider__dot').forEach((dot, i) => {
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
        // Spela bara upp videor som syns
        this.track.querySelectorAll('video').forEach((video) => {
          const rect = video.getBoundingClientRect();
          const trackRect = this.track.getBoundingClientRect();
          const visible = rect.right > trackRect.left + 10 && rect.left < trackRect.right - 10;
          if (visible) video.play().catch(() => {});
          else video.pause();
        });
      }
    }
  );
}

if (!customElements.get('bg-before-after')) {
  customElements.define(
    'bg-before-after',
    class BgBeforeAfter extends HTMLElement {
      connectedCallback() {
        const range = this.querySelector('input[type="range"]');
        if (!range) return;
        const set = () => this.style.setProperty('--bg-ba-pos', `${range.value}%`);
        range.addEventListener('input', set);
        set();
      }
    }
  );
}

if (!customElements.get('bg-gallery')) {
  customElements.define(
    'bg-gallery',
    class BgGallery extends HTMLElement {
      connectedCallback() {
        this.main = this.querySelector('[data-bg-main]');
        this.thumbs = Array.from(this.querySelectorAll('[data-bg-thumb]'));
        this.thumbs.forEach((thumb, i) =>
          thumb.addEventListener('click', () => {
            this.main.scrollTo({ left: i * this.main.clientWidth, behavior: 'smooth' });
          })
        );
        this.main?.addEventListener(
          'scroll',
          () => {
            const index = Math.round(this.main.scrollLeft / this.main.clientWidth);
            this.thumbs.forEach((t, i) => t.setAttribute('aria-current', i === index ? 'true' : 'false'));
            const active = this.thumbs[index];
            if (active) active.parentElement.scrollTo({ left: active.offsetLeft - 40, behavior: 'smooth' });
          },
          { passive: true }
        );
      }
    }
  );
}

if (!customElements.get('bg-buy-box')) {
  customElements.define(
    'bg-buy-box',
    class BgBuyBox extends HTMLElement {
      connectedCallback() {
        this.idInput = this.querySelector('input[name="id"]');
        this.planInput = this.querySelector('input[name="selling_plan"]');
        this.subToggle = this.querySelector('[data-bg-sub-toggle]');
        this.subSelect = this.querySelector('[data-bg-sub-select]');
        this.subWrap = this.querySelector('[data-bg-sub]');
        this.querySelectorAll('[data-bg-variant]').forEach((radio) =>
          radio.addEventListener('change', () => this.onVariant(radio))
        );
        this.subToggle?.addEventListener('change', () => this.syncPlan());
        this.subSelect?.addEventListener('change', () => {
          if (this.subToggle && !this.subToggle.checked) this.subToggle.checked = true;
          this.syncPlan();
        });
        this.syncPlan();
      }

      onVariant(radio) {
        if (!radio.checked) return;
        this.idInput.value = radio.value;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', radio.value);
        window.history.replaceState({}, '', url.toString());
        this.updatePriceRow(radio);
        this.syncPlan();
      }

      updatePriceRow(radio) {
        const row = this.querySelector('[data-bg-price-row]');
        if (!row || !radio.dataset.price) return;
        const price = parseInt(radio.dataset.price, 10);
        const compare = parseInt(radio.dataset.compare || '0', 10);
        const now = row.querySelector('.bg-price__now');
        if (now) now.textContent = this.format(price, true);
        let was = row.querySelector('.bg-price__was');
        let badge = row.querySelector('.bg-price__badge');
        const onSale = compare > price;
        if (was) was.hidden = !onSale;
        if (badge) badge.hidden = !onSale;
        if (!onSale) return;
        if (was) was.textContent = this.format(compare, true);
        const label = badge?.querySelector('span');
        if (label) label.textContent = `${row.dataset.badgePrefix} ${Math.floor(((compare - price) * 100) / compare)}%`;
      }

      syncPlan() {
        if (!this.planInput) return;
        const active = this.subToggle ? this.subToggle.checked : false;
        if (this.subWrap) this.subWrap.dataset.active = active ? 'true' : 'false';
        this.planInput.value = active && this.subSelect ? this.subSelect.value : '';
        this.planInput.disabled = !this.planInput.value;
        this.updatePrices(active);
      }

      updatePrices(active) {
        const option = active && this.subSelect ? this.subSelect.selectedOptions[0] : null;
        const discount = option ? parseFloat(option.dataset.discount || 0) : 0;
        const fixed = option?.dataset.discountType === 'fixed_amount';
        this.querySelectorAll('[data-bg-variant]').forEach((radio) => {
          const card = radio.closest('.bg-bundle');
          const priceEl = card?.querySelector('[data-bg-price]');
          if (!priceEl) return;
          const base = parseInt(priceEl.dataset.base, 10);
          let cents = base;
          if (discount) cents = fixed ? Math.max(0, base - discount) : Math.round(base * (1 - discount / 100));
          priceEl.textContent = this.format(cents);
        });
      }

      format(cents, decimals = false) {
        const kr = cents / 100;
        const text = Number.isInteger(kr) && !decimals ? kr.toLocaleString('sv-SE') : kr.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return `${text} kr`;
      }
    }
  );
}

/* Korta prenumerationsnamn (t.ex. i Kaching): "Leverera var 2:e vecka, 24,50 kr i rabatt" -> "Var 2:e vecka" */
(function () {
  const pattern = /^(\s*)Leverera\s+(var\s[^,]+?)\s*(,.*rabatt\s*)?$/i;
  const shorten = (node) => {
    const m = node.nodeValue && node.nodeValue.match(pattern);
    if (!m) return;
    const text = m[2].trim();
    node.nodeValue = m[1] + text.charAt(0).toUpperCase() + text.slice(1);
  };
  const scan = (root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) shorten(node);
    root.querySelectorAll?.('*').forEach((el) => {
      if (el.shadowRoot && !el.shadowRoot.__bgPlanObserved) {
        el.shadowRoot.__bgPlanObserved = true;
        scan(el.shadowRoot);
        observe(el.shadowRoot);
      }
    });
  };
  const observe = (root) => {
    new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        if (m.type === 'characterData') shorten(m.target);
        m.addedNodes.forEach((n) => {
          if (n.nodeType === Node.TEXT_NODE) shorten(n);
          else if (n.nodeType === Node.ELEMENT_NODE) scan(n);
        });
      });
    }).observe(root, { childList: true, subtree: true, characterData: true });
  };
  const start = () => {
    scan(document.body);
    observe(document.body);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/* Nedräkningstimer i Kaching-blocket (samma som Tyskland: 8 min per besökare, startar om) */
(function () {
  // Avstängd på Jonas begäran (11 okt 2026). Lägg till en rad här för att visa timern på en produkt igen.
  const TIMERS = {};
  const box = document.querySelector('bg-buy-box[data-product-handle]');
  const cfg = box && TIMERS[box.dataset.productHandle];
  if (!cfg) return;
  const key = 'bg-timer-' + box.dataset.productHandle;
  const duration = cfg.minutes * 60 * 1000;
  const endTime = () => {
    let end = 0;
    try { end = parseInt(sessionStorage.getItem(key), 10) || 0; } catch (e) {}
    if (!end || end < Date.now()) {
      end = Date.now() + duration;
      try { sessionStorage.setItem(key, String(end)); } catch (e) {}
    }
    return end;
  };
  const render = (el) => {
    const left = Math.max(0, endTime() - Date.now());
    const m = String(Math.floor(left / 60000)).padStart(2, '0');
    const s = String(Math.floor((left % 60000) / 1000)).padStart(2, '0');
    el.textContent = cfg.title.replace('{{timer}}', m + ':' + s);
  };
  const mount = () => {
    const title = document.querySelector('.kaching-bundles__block-title');
    if (!title || title.parentNode.querySelector('.bg-kaching-timer')) return;
    const el = document.createElement('div');
    el.className = 'bg-kaching-timer';
    title.insertAdjacentElement('afterend', el);
    render(el);
    setInterval(() => render(el), 1000);
  };
  new MutationObserver(mount).observe(document.body, { childList: true, subtree: true });
  mount();
})();

/* Rullande banners: samma lugna hastighet (px/s) överallt, oavsett textlängd och skärmbredd */
(function () {
  const PX_PER_SECOND = 40;
  const apply = () => {
    document.querySelectorAll('.bg-ticker').forEach((ticker) => {
      const group = ticker.querySelector('.bg-ticker__group');
      const width = group ? group.getBoundingClientRect().width : 0;
      if (width > 0) ticker.style.setProperty('--bg-ticker-duration', (width / PX_PER_SECOND).toFixed(1) + 's');
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply);
  else apply();
  window.addEventListener('load', apply);
  let t;
  window.addEventListener('resize', () => { clearTimeout(t); t = setTimeout(apply, 200); });
})();

/* Styckpris i Kaching-rutorna: tunt streck under totalpriset och sedan "124,50 kr / st" (totalpris delat på antal) */
(function () {
  let qtyById = null;
  const quantities = () => {
    if (qtyById) return qtyById;
    qtyById = {};
    document.querySelectorAll('script.kaching-bundles-deal-block-settings').forEach((s) => {
      try { (JSON.parse(s.textContent).dealBars || []).forEach((b) => { if (b.id) qtyById[b.id] = Number(b.quantity) || 0; }); } catch (e) {}
    });
    return qtyById;
  };
  const fmt = (v) => (Math.round(v * 100) / 100).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr / st';
  const apply = () => {
    document.querySelectorAll('.kaching-bundles__bar').forEach((bar) => {
      if (bar.closest('.kaching-bundles__subscriptions')) return;
      const pricing = bar.querySelector('.kaching-bundles__bar-pricing');
      const priceEl = bar.querySelector('.kaching-bundles__bar-price');
      if (!pricing || !priceEl) return;
      const qty = quantities()[bar.dataset.dealBarId] || parseInt((bar.querySelector('.kaching-bundles__bar-title')?.textContent || '').trim(), 10) || 0;
      const total = parseFloat(priceEl.textContent.replace(/[^\d,.]/g, '').replace(',', '.'));
      let unit = pricing.querySelector('.bg-unit');
      if (qty < 2 || !(total > 0)) { unit?.remove(); return; }
      const text = fmt(total / qty);
      if (!unit) {
        unit = document.createElement('div');
        unit.className = 'bg-unit';
        unit.innerHTML = '<span class="bg-unit__line"></span><span class="bg-unit__text"></span>';
        pricing.appendChild(unit);
      }
      const label = unit.querySelector('.bg-unit__text');
      if (label.textContent !== text) label.textContent = text;
    });
  };
  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; apply(); });
  };
  const start = () => {
    if (!document.querySelector('script.kaching-bundles-deal-block-settings')) return;
    apply();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, characterData: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
